import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { executeRecesion } from '../../../../../../../../../lib/api/reSell/factoring';
import { getAECsInPosession } from '../../../../../../../../../lib/api/reSell/factoring';
import CustomButton from '../../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../../lib/designSystem/CircularProgress';
import EnhancedTable from '../../../../../../../../../lib/designSystem/Table';
import { showAlert } from '../../../../../../../../../lib/functions/utils';
import { RootState } from '../../../../../../../../../services/redux/store';
import {
  CessionDocument,
  CessionDocumentPayload,
  CessionType,
  FundCessionRequest,
  ResellSuggestedDocument,
  SimpleCessionDialogProps,
} from '../../types';

const SimpleCessionDialog: React.FC<SimpleCessionDialogProps> = ({
  open,
  setOpen,
  documents,
  bankAccountId,
  funds = [],
  amountToReceiveInput,
  setUpMainPage,
  resellsToApply,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [documentsWithStatus, setDocumentsWithStatus] = useState<
    CessionDocument[]
  >([]);

  const canMakeCession = documentsWithStatus.every(
    (doc) => doc.status === true,
  );

  const processDocuments = async () => {
    if (open && documents.length > 0) {
      setInitialLoading(true);
      try {
        const documentForResell = documents.map((doc) => ({
          id: doc.id,
          businessIdentifier: doc.businessIdentifier,
          folio: doc.folio,
          cessionDate: doc.cessionDate,
        }));
        const response = await getAECsInPosession(dispatch, documentForResell);
        const processedDocuments = documents.map((doc) => {
          const fund = funds.find((f) => f.id === doc.fundId);
          const documentWithStatus = response.find(
            (item: any) => item.id === doc.id,
          );
          return {
            ...doc,
            subType: documentWithStatus.subType,
            fundName: fund?.name,
            status: documentWithStatus?.status,
            message: documentWithStatus?.message,
          };
        });
        setDocumentsWithStatus(processedDocuments);
      } catch (error) {
        console.error('Error al obtener estado de documentos:', error);
        showAlert(dispatch, 'error', 'Error al obtener estado de documentos');
      } finally {
        setInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    processDocuments();
  }, []);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const resellsToApplyPayload = resellsToApply.map(
        (resell: ResellSuggestedDocument) => ({
          resellId: resell.id,
          amount: Number(resell.amountToConciliate || 0),
        }),
      );

      const payload: FundCessionRequest = {
        type: CessionType.FUND,
        amountToReceive: amountToReceiveInput,
        resellsToApply: resellsToApplyPayload,
        documents: documentsWithStatus.map(
          (document: CessionDocument): CessionDocumentPayload => ({
            id: document.id,
            folio: document.folio,
            businessIdentifier: document.businessIdentifier,
            stakeholderIdentifier:
              document.stakeholderIdentifier ?? document.businessIdentifier,
            amount: document.amount ?? 0,
            originalAmount: document.originalAmount,
            advance: document.advance,
            arrearRate: document.arrearRate,
            subType: document.subType ?? '33',
            bankAccountId: bankAccountId,
            cessionDate: document.cessionDate,
            startDate: document.startDate,
            endDate: document.endDate,
            rate: document.rate?.toString() ?? '',
            rateCost: document.rateCost?.toString() ?? '',
            operationCostFixed: document.operationCostFixed?.toString(),
            operationCostVariable: document.operationCostVariable?.toString(),
            fund: {
              id: document.fundId ?? '',
              name: document.fundName ?? '',
            },
          }),
        ),
      };

      const response: any = await executeRecesion(dispatch, payload);

      if (response.success) {
        showAlert(dispatch, 'success', 'Cesión ejecutada correctamente');
        await setUpMainPage();
        setOpen(false);
      } else {
        showAlert(
          dispatch,
          'error',
          response.message ?? 'Error al ejecutar la cesión',
        );
      }
    } catch (error: any) {
      showAlert(dispatch, 'error', 'Error al ejecutar la cesión');
      console.error('Error en cesión:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog maxWidth="lg" fullWidth open={open}>
      <DialogTitle>
        <Typography variant="h6">Cesión de facturas</Typography>
      </DialogTitle>
      <DialogContent>
        {initialLoading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
            }}
          >
            <CenteredCircularProgress type="dialog" />
            <Typography
              variant="body2"
              color="text.secondary"
              style={{ marginTop: '16px', textAlign: 'center' }}
            >
              Cargando cesiones disponibles...
            </Typography>
          </div>
        ) : loading ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <div>
            <Typography variant="body1" style={{ marginBottom: '15px' }}>
              A continuación se muestran todas las facturas que van a ser
              cedidas:
            </Typography>
            {documentsWithStatus.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay facturas para mostrar. Asegúrate de haber seleccionado
                cesiones en el paso anterior.
              </Typography>
            ) : (
              <EnhancedTable
                data={documentsWithStatus}
                dataHeaders={[
                  {
                    key: 'businessName',
                    label: 'Razón social',
                  },
                  {
                    key: 'businessIdentifier',
                    label: 'RUT',
                  },
                  {
                    key: 'folio',
                    label: 'Folio',
                  },
                  {
                    key: 'cessionDate',
                    label: 'Cesión',
                    type: 'date',
                  },
                  {
                    key: 'subType',
                    label: 'Código',
                  },
                  {
                    key: 'amount',
                    label: 'Monto',
                    type: 'money',
                  },
                  {
                    key: 'fundName',
                    label: 'Fondo',
                  },
                  {
                    key: 'fundId',
                    label: 'RUT',
                  },
                  {
                    key: 'status',
                    label: 'Estado',
                    type: 'yes-no-circle',
                  },
                  {
                    key: 'message',
                    label: 'Mensaje',
                  },
                ]}
              />
            )}
          </div>
        )}
      </DialogContent>
      {!loading && !initialLoading && (
        <DialogActions>
          <CustomButton onClick={() => setOpen(false)} color="secondary">
            Salir
          </CustomButton>
          <CustomButton
            disabled={!canMakeCession}
            onClick={() => handleSubmit()}
            color="primary"
          >
            Confirmar
          </CustomButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SimpleCessionDialog;
